import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CircleMarker, MapContainer, Rectangle, TileLayer, Tooltip } from 'react-leaflet';

import { dashboardService } from '../../services/dashboard';
import { RegionalLiveRiskResponse } from '../../types';

const SPECIES_OPTIONS = [
  'All Species',
  'Apis mellifera',
  'Bombus terrestris',
  'Pieris rapae',
  'Vanessa atalanta',
  'Papilio xuthus',
  'Danaus plexippus',
  'Bombus impatiens',
  'Vanessa cardui',
];

const REGION_GEOMETRY: Record<
  string,
  {
    center: [number, number];
    bounds: [[number, number], [number, number]];
  }
> = {
  Europe: {
    center: [54, 15],
    bounds: [[34, -12], [72, 45]],
  },
  Asia: {
    center: [35, 95],
    bounds: [[5, 45], [75, 145]],
  },
  'North America': {
    center: [48, -103],
    bounds: [[12, -168], [75, -52]],
  },
  'South America': {
    center: [-16, -60],
    bounds: [[-56, -82], [13, -34]],
  },
  Africa: {
    center: [2, 20],
    bounds: [[-35, -18], [37, 52]],
  },
  Oceania: {
    center: [-25, 135],
    bounds: [[-50, 110], [0, 180]],
  },
};

const colorFromScore = (score: number): string => {
  if (score >= 70) return '#cf4d4d';
  if (score >= 45) return '#e08e2b';
  return '#2a8f57';
};

const RiskMapPage: React.FC = () => {
  const [species, setSpecies] = useState('All Species');
  const [yearFrom, setYearFrom] = useState('2021');
  const [yearTo, setYearTo] = useState('2026');
  const [limit, setLimit] = useState('700');
  const [data, setData] = useState<RegionalLiveRiskResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const loadRegionalRisk = useCallback(async () => {
    setError('');
    setLoading(true);

    const from = Number(yearFrom);
    const to = Number(yearTo);
    if (Number.isFinite(from) && Number.isFinite(to) && from > to) {
      setError('Year From cannot be greater than Year To.');
      setLoading(false);
      return;
    }

    try {
      const response = await dashboardService.getRegionalLiveRisk(
        Number(yearFrom) || undefined,
        Number(yearTo) || undefined,
        species === 'All Species' ? undefined : species,
        Number(limit) || 700,
      );
      setData(response);
    } catch (err: any) {
      const detail = err?.response?.data?.detail || err?.message;
      setError(detail || 'Failed to load live regional risk data. Try a smaller record limit.');
    } finally {
      setLoading(false);
    }
  }, [limit, species, yearFrom, yearTo]);

  useEffect(() => {
    void loadRegionalRisk();
  }, [loadRegionalRisk]);

  const topRegion = useMemo(() => {
    if (!data?.regions?.length) {
      return null;
    }
    return data.regions[0];
  }, [data]);

  const maxRiskScore = useMemo(() => {
    if (!data?.regions?.length) {
      return 100;
    }

    return Math.max(...data.regions.map((region) => region.risk_score), 1);
  }, [data]);

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    await loadRegionalRisk();
  };

  return (
    <section className="feature-page riskmap-premium">
      <div className="riskmap-hero">
        <div className="riskmap-hero-copy">
          <p className="riskmap-kicker">Global Monitoring</p>
          <h1>Live Regional Risk Map</h1>
          <p>Real-time biodiversity risk by region using live GBIF + NASA signals.</p>
        </div>

        <div className="riskmap-hero-metrics" aria-label="Risk map snapshot">
          <article className="riskmap-hero-tile">
            <h3><span className="hero-tile-icon" aria-hidden="true">◌</span> Top Region</h3>
            <p>{topRegion?.region ?? 'Loading...'}</p>
          </article>
          <article className="riskmap-hero-tile">
            <h3><span className="hero-tile-icon" aria-hidden="true">◍</span> Top Risk</h3>
            <p>{topRegion ? `${topRegion.risk_score}` : '...'}</p>
          </article>
          <article className="riskmap-hero-tile">
            <h3><span className="hero-tile-icon" aria-hidden="true">◈</span> Records</h3>
            <p>{data?.total_sampled_records ?? 0}</p>
          </article>
          <article className="riskmap-hero-tile">
            <h3><span className="hero-tile-icon" aria-hidden="true">◎</span> Species Scope</h3>
            <p>{species === 'All Species' ? 'All' : species}</p>
          </article>
        </div>
      </div>

      <form className="bbox-form" onSubmit={onSubmit}>
        <label>
          Species
          <select value={species} onChange={(e) => setSpecies(e.target.value)}>
            {SPECIES_OPTIONS.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </label>
        <label>
          Year From
          <input value={yearFrom} onChange={(e) => setYearFrom(e.target.value)} type="number" min={1900} max={2100} />
        </label>
        <label>
          Year To
          <input value={yearTo} onChange={(e) => setYearTo(e.target.value)} type="number" min={1900} max={2100} />
        </label>
        <label>
          Record Limit
          <select value={limit} onChange={(e) => setLimit(e.target.value)}>
            <option value="300">Top 300</option>
            <option value="500">Top 500</option>
            <option value="700">Top 700</option>
            <option value="1000">Top 1000</option>
          </select>
        </label>

        <button type="submit" disabled={loading}>
          {loading ? 'Refreshing...' : 'Refresh Live Risk'}
        </button>
      </form>

      {error && <p className="form-error">{error}</p>}

      {data && (
        <div className="panel risk-map-summary">
          <h2>Live Feed Summary</h2>
          <p>
            Source: {data.source.toUpperCase()} | Total sampled records: {data.total_sampled_records} | Updated: {new Date(data.updated_at).toLocaleString()}
          </p>
          {topRegion && (
            <p>
              Highest current pressure: <strong>{topRegion.region}</strong> ({topRegion.risk_level}, score {topRegion.risk_score})
            </p>
          )}
        </div>
      )}

      {data && (
        <section className="panel world-risk-map-panel">
          <h2>Global Heat Map</h2>
          <p>Regional overlays are shaded by biodiversity risk intensity.</p>

          <MapContainer
            center={[20, 10]}
            zoom={2}
            minZoom={2}
            maxZoom={5}
            zoomControl={true}
            scrollWheelZoom={true}
            className="world-risk-map"
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />

            {data.regions.map((region) => {
              const geometry = REGION_GEOMETRY[region.region];
              if (!geometry) {
                return null;
              }

              const regionColor = colorFromScore(region.risk_score);
              const intensity = Math.max(0.2, Math.min(0.85, region.risk_score / maxRiskScore));

              return (
                <React.Fragment key={region.region}>
                  <Rectangle
                    bounds={geometry.bounds}
                    pathOptions={{
                      color: regionColor,
                      weight: 2,
                      fillColor: regionColor,
                      fillOpacity: intensity,
                    }}
                  >
                    <Tooltip direction="center" sticky>
                      <div className="map-tooltip">
                        <strong>{region.region}</strong>
                        <br />
                        Risk: {region.risk_level} ({region.risk_score})
                        <br />
                        Records: {region.record_count}
                      </div>
                    </Tooltip>
                  </Rectangle>

                  <CircleMarker
                    center={geometry.center}
                    radius={7}
                    pathOptions={{
                      color: '#10252f',
                      weight: 1,
                      fillColor: regionColor,
                      fillOpacity: 0.95,
                    }}
                  >
                    <Tooltip>
                      {region.region}: {region.risk_score}
                    </Tooltip>
                  </CircleMarker>
                </React.Fragment>
              );
            })}
          </MapContainer>

          <div className="map-legend">
            <span><i className="legend-dot legend-low" /> Low</span>
            <span><i className="legend-dot legend-medium" /> Medium</span>
            <span><i className="legend-dot legend-high" /> High</span>
          </div>
        </section>
      )}

      {data && (
        <div className="region-risk-grid">
          {data.regions.map((region) => (
            <article key={region.region} className={`region-risk-card risk-${region.risk_level.toLowerCase()}`}>
              <div className="region-risk-top">
                <h3>{region.region}</h3>
                <span className={`status-badge status-${region.risk_level.toLowerCase()}`}>{region.risk_level}</span>
              </div>

              <p className="region-risk-score">Score {region.risk_score}</p>

              <div className="region-risk-stats">
                <p><span>Records</span><strong>{region.record_count}</strong></p>
                <p><span>Share</span><strong>{region.share_percent}%</strong></p>
                <p><span>Dominant species</span><strong>{region.dominant_species}</strong></p>
                <p><span>Avg temp</span><strong>{region.avg_temperature_celsius}°C</strong></p>
                <p><span>Humidity</span><strong>{region.avg_humidity_percent}%</strong></p>
                <p><span>Vegetation</span><strong>{region.vegetation_index}</strong></p>
              </div>
            </article>
          ))}
        </div>
      )}
    </section>
  );
};

export default RiskMapPage;
